import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import {
  DSPageHeader, DSCard, DSButton, DSInput, DSAlert, DSSheet, DSSelect
} from '../design-system/primitives';
import { showToast } from '../components/Toast';
import './Dashboard.css';
import './RulesCompliance.css';

// Admin view of the documents members sign: the club rules and the membership
// agreement.
//
// Content lives in WordPress. This screen is who has agreed to it, and the one
// action that changes that: asking everyone to sign again. That is deliberately
// a decision rather than a side effect of editing the page - content gets edited
// for typos and formatting, and if every edit re-prompted everybody the prompt
// would stop meaning anything.
//
// Both documents are shown at once rather than behind a switcher. There are two
// of them, an admin's question is usually "where does the club stand", and
// hiding one behind a tab makes the one you are not looking at easy to forget.

const DOCUMENTS = [
  { key: 'club_rules', label: 'Club rules' },
  { key: 'membership_agreement', label: 'Membership agreement' }
];

const CHANGE_KINDS = [
  { value: 'added', label: 'Added' },
  { value: 'changed', label: 'Changed' },
  { value: 'clearer', label: 'Clearer' }
];

function DocumentPanel({ doc, onRequire, reloadKey }) {
  const [compliance, setCompliance] = useState(null);
  const [outstanding, setOutstanding] = useState([]);
  const [published, setPublished] = useState(null);
  const [showOutstanding, setShowOutstanding] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [complianceRes, outstandingRes, publishedRes] = await Promise.all([
        apiService.getRulesCompliance(doc.key),
        apiService.getRulesOutstanding(doc.key).catch(() => ({ data: [] })),
        apiService.getDocument(doc.key).catch(() => ({ data: null }))
      ]);
      setCompliance(complianceRes.data);
      setOutstanding(outstandingRes.data || []);
      setPublished(publishedRes.data);
    } catch (err) {
      showToast(`Failed to load ${doc.label.toLowerCase()}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [doc.key, doc.label]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const markReviewed = async () => {
    try {
      await apiService.markRulesReviewed({
        documentKey: doc.key,
        modifiedAt: published?.modified
      });
      showToast('Marked as reviewed. Nobody was asked to sign again.', 'success');
      load();
    } catch (err) {
      showToast('Failed to mark as reviewed', 'error');
    }
  };

  if (loading) {
    return (
      <DSCard style={{ marginBottom: '16px' }}>
        <p className="rc-muted">Loading {doc.label.toLowerCase()}...</p>
      </DSCard>
    );
  }

  const total = compliance?.total ?? 0;
  const current = compliance?.current ?? 0;
  const behind = compliance?.outstanding ?? 0;
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;

  // The WordPress page carries a modified timestamp; source_modified_at records
  // which revision was last reviewed here. A newer page means somebody edited it
  // and nobody has decided yet whether that warrants asking the club to sign
  // again. Surfaced as a question, never acted on automatically: auto-prompting
  // on every edit would train people to click through without reading.
  // Both flags come from the server, which compares the WordPress timestamp
  // against the reviewed marker inside Postgres. Doing it here meant comparing a
  // naive site-local time from WordPress against a value node-postgres parses as
  // UTC, which is wrong by the UTC offset - the banner would not clear after an
  // admin had acted on it.
  //
  // Two situations, not one. Saying "was edited on X" when nobody has ever
  // reviewed it reads as though somebody just changed the rules, which on a
  // fresh install is untrue.
  const publishedModified = published?.modified ? new Date(published.modified) : null;
  const neverReviewed = Boolean(published?.neverReviewed);
  const editedSinceReview = Boolean(published?.editedSinceReview);
  const sourceChanged = neverReviewed || editedSinceReview;

  return (
    <DSCard style={{ marginBottom: '16px' }}>
      <div className="rc-head">
        <h3 className="rc-title">{doc.label}</h3>
        {compliance?.effectiveDate && (
          <span className="rc-stamp">
            In effect {new Date(`${compliance.effectiveDate}T00:00:00`).toLocaleDateString()}
          </span>
        )}
      </div>

      <div className="rc-progress-head">
        <span className="rc-muted">Signed the current version</span>
        <span className="rc-count">{current} of {total}</span>
      </div>
      <div className="rc-track" role="img" aria-label={`${current} of ${total} members signed`}>
        <span className="rc-track__fill" style={{ width: `${pct}%` }} />
      </div>

      {behind > 0 ? (
        <button type="button" className="rc-link" onClick={() => setShowOutstanding(true)}>
          {behind} {behind === 1 ? 'member has' : 'members have'} not signed
        </button>
      ) : (
        <p className="rc-allgood">Everyone has signed the current version.</p>
      )}

      {/* Members keep everything while they are behind. Worth stating where an
          admin might otherwise assume the prompt has teeth it does not: the
          enforcement is the login gate, not membership status. */}
      <p className="rc-note">
        Members who have not signed keep their membership and their door access.
        They are asked the next time they open the app. Guests are never asked,
        and applicants sign as part of applying.
      </p>

      {sourceChanged && (
        <DSAlert tone="warning" style={{ marginTop: '12px' }}>
          {editedSinceReview ? (
            <>
              The page in WordPress was edited on{' '}
              <strong>{publishedModified.toLocaleDateString()}</strong>, after the last
              time it was reviewed here. If the change was substantive, ask everyone to
              sign again. If it was a typo or formatting, mark it reviewed and nobody is
              asked.
            </>
          ) : (
            <>
              This has not been reviewed here yet. Once you review it, an edit in
              WordPress will show up on this screen as a change to decide on. Nothing is
              wrong, and nobody is being asked because of this.
            </>
          )}
          <div style={{ marginTop: '12px' }}>
            <DSButton appearance="outline" tone="neutral" onClick={markReviewed}>
              {editedSinceReview ? 'Mark reviewed, do not re-prompt' : 'Mark as reviewed'}
            </DSButton>
          </div>
        </DSAlert>
      )}

      <div className="rc-actions">
        <DSButton appearance="solid" tone="primary" onClick={() => onRequire(doc)}>
          Ask everyone to sign again
        </DSButton>
      </div>

      {showOutstanding && (
        <DSSheet
          title={`Still to sign the ${doc.label.toLowerCase()}`}
          onClose={() => setShowOutstanding(false)}
        >
          <p className="rc-muted" style={{ marginTop: 0 }}>
            They keep full access. The app asks each time they open it, and an
            admin can ask in person.
          </p>
          {outstanding.length === 0 && <p className="rc-muted">Nobody. Everyone is current.</p>}
          {outstanding.map((person) => (
            <div className="rc-person" key={person.id}>
              <span className="rc-person__main">
                <span className="rc-person__name">{person.name}</span>
                <span className="rc-person__meta">
                  {person.lastAcknowledgedAt
                    ? `Signed an earlier version on ${new Date(person.lastAcknowledgedAt).toLocaleDateString()}`
                    : 'Never signed'}
                </span>
              </span>
              {!person.lastAcknowledgedAt && (
                <span className="rc-person__tag">Never signed</span>
              )}
            </div>
          ))}
        </DSSheet>
      )}
    </DSCard>
  );
}

function RulesCompliance() {
  const [requiring, setRequiring] = useState(null);
  const today = new Date().toISOString().split('T')[0];
  const [effectiveDate, setEffectiveDate] = useState(today);
  const [changes, setChanges] = useState([{ kind: 'changed', text: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const openRequire = (doc) => {
    setEffectiveDate(today);
    setChanges([{ kind: 'changed', text: '' }]);
    setRequiring(doc);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const response = await apiService.requireRulesAcknowledgment({
        documentKey: requiring.key,
        effectiveDate,
        changeSummary: changes.filter((c) => c.text.trim())
      });
      const count = response.data?.compliance?.outstanding ?? 0;
      showToast(
        `${count} ${count === 1 ? 'member' : 'members'} will be asked to sign the `
        + `${requiring.label.toLowerCase()} again`,
        'success'
      );
      setRequiring(null);
      setReloadKey((n) => n + 1);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to ask for signatures', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <DSPageHeader
        title="Rules and agreements"
        description="Content lives in WordPress. This is who has agreed to it."
      />

      {DOCUMENTS.map((doc) => (
        <DocumentPanel key={doc.key} doc={doc} onRequire={openRequire} reloadKey={reloadKey} />
      ))}

      {requiring && (
        <DSSheet
          title={`Ask everyone to sign the ${requiring.label.toLowerCase()} again`}
          onClose={() => setRequiring(null)}
          footer={
            <div style={{ display: 'flex', gap: '10px' }}>
              <DSButton
                appearance="outline"
                tone="neutral"
                onClick={() => setRequiring(null)}
                disabled={submitting}
              >
                Cancel
              </DSButton>
              <DSButton appearance="solid" tone="primary" onClick={submit} disabled={submitting}>
                {submitting ? 'Working...' : 'Ask everyone to sign again'}
              </DSButton>
            </div>
          }
        >
          <p className="rc-muted" style={{ marginTop: 0 }}>
            Every member who signed before this date is asked to read and sign
            again. Nobody loses their membership or their door access. Members
            who have never signed are already being asked.
          </p>

          <DSInput
            label="In effect from"
            type="date"
            value={effectiveDate}
            max={today}
            onChange={(e) => setEffectiveDate(e.target.value)}
            helpText="Usually today. An earlier date also catches people who signed since then."
          />

          {/* The bullets a member sees on the re-sign screen. Optional, but the
              difference between a re-signature that means something and one
              where everybody scrolls to the bottom and types their name. */}
          <p className="rc-subhead">What changed</p>
          <p className="rc-muted" style={{ marginTop: 0 }}>
            Shown to members when they are asked. Leave blank if you would rather
            not say.
          </p>

          {changes.map((change, index) => (
            <div className="rc-change" key={index}>
              <DSSelect
                label={index === 0 ? 'Kind' : ''}
                value={change.kind}
                onChange={(e) => setChanges((list) => list.map((c, i) => (
                  i === index ? { ...c, kind: e.target.value } : c
                )))}
              >
                {CHANGE_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </DSSelect>
              <DSInput
                label={index === 0 ? 'What changed' : ''}
                value={change.text}
                placeholder="Dues are stated as $50 a month, up from $40."
                onChange={(e) => setChanges((list) => list.map((c, i) => (
                  i === index ? { ...c, text: e.target.value } : c
                )))}
              />
            </div>
          ))}

          {changes.length < 10 && (
            <DSButton
              appearance="outline"
              tone="neutral"
              size="sm"
              onClick={() => setChanges((list) => [...list, { kind: 'changed', text: '' }])}
            >
              Add another
            </DSButton>
          )}
        </DSSheet>
      )}
    </div>
  );
}

export default RulesCompliance;
